import React, { useState, useEffect, useRef } from 'react';
import { useProject } from '../context/ProjectContext';
import axios from 'axios';
import jsPDF from 'jspdf';
import { getApiUrl, axiosConfig } from '../config/api';
import './CoreWebVitals.css';
import '../components/PageLoader.css';

// SEO and website fun facts
const funFacts = [
  "Did you know? Google processes over 8.5 billion searches per day!",
  "Did you know? 75% of users never scroll past the first page of search results.",
  "Did you know? Websites that load in 2 seconds have a 9% bounce rate, while those taking 5 seconds have a 38% bounce rate.",
  "Did you know? Mobile-first indexing means Google primarily uses the mobile version of your site for ranking.",
  "Did you know? Pages with images get 94% more views than those without.",
  "Did you know? The average user reads only 20% of the text on a webpage.",
  "Did you know? Websites with SSL certificates (HTTPS) rank higher in Google search results.",
  "Did you know? The first 3 seconds of page load time impact 53% of mobile site visits.",
  "Did you know? Internal linking can improve your site's SEO and help users navigate better.",
  "Did you know? Long-form content (2000+ words) tends to rank higher in search results.",
  "Did you know? Schema markup can help your website appear in rich snippets, increasing click-through rates.",
  "Did you know? Core Web Vitals are now a ranking factor for Google search results.",
  "Did you know? Websites with fast loading times have better conversion rates.",
  "Did you know? Alt text for images not only helps SEO but also improves accessibility.",
  "Did you know? A well-structured sitemap helps search engines crawl and index your site more effectively."
];

// Circular Progress Component
const CircularProgress = ({ percentage, size = 120, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 90) return '#22c55e'; // green
    if (percentage >= 50) return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  return (
    <div className="circular-progress-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="circular-progress-svg">
        <circle
          className="circular-progress-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-progress-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={getColor()}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="circular-progress-text">
        <span className="circular-progress-percentage">{Math.round(percentage)}</span>
      </div>
    </div>
  );
};

// Helper function to get cache key for a URL and device
const getCacheKey = (targetUrl, deviceStrategy) => {
  if (!targetUrl) return null;
  // Normalize URL to ensure consistent cache keys
  const normalizedUrl = targetUrl.toLowerCase().trim().replace(/\/$/, '');
  return `coreWebVitals_${normalizedUrl}_${deviceStrategy}`;
};

// Helper function to get countdown key for analyze action
const getAnalyzeCountdownKey = (projectUrl) => {
  if (!projectUrl) return null;
  const normalizedUrl = projectUrl.toLowerCase().trim().replace(/\/$/, '');
  return `coreWebVitals_countdown_analyze_${normalizedUrl}`;
};

// Helper function to get countdown key for download action
const getDownloadCountdownKey = (projectUrl) => {
  if (!projectUrl) return null;
  const normalizedUrl = projectUrl.toLowerCase().trim().replace(/\/$/, '');
  return `coreWebVitals_countdown_download_${normalizedUrl}`;
};

const CoreWebVitals = ({ user, onLogout, currentProject }) => {
  const { updateProject, userPlan } = useProject();
  const reportRef = useRef(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [desktopResults, setDesktopResults] = useState(null);
  const [mobileResults, setMobileResults] = useState(null);
  const [viewDevice, setViewDevice] = useState('desktop'); // 'desktop' or 'mobile' - for viewing only
  const [expandedInsights, setExpandedInsights] = useState(new Set()); // Track which insights are expanded
  const [expandedCategories, setExpandedCategories] = useState(new Set(['performance', 'accessibility', 'bestPractices', 'seo'])); // Track which categories are expanded (default all expanded)
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [funFact, setFunFact] = useState('');
  const [analyzeCountdown, setAnalyzeCountdown] = useState(0);
  const [downloadCountdown, setDownloadCountdown] = useState(0);

  // Load cached results on mount or when URL/device changes
  useEffect(() => {
    if (currentProject?.url) {
      setUrl(currentProject.url);
    }
  }, [currentProject]);

  // Check analyze countdown on mount and when project/url changes
  useEffect(() => {
    const targetUrl = currentProject?.url || url || null;
    if (!targetUrl) {
      setAnalyzeCountdown(0);
      return;
    }

    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) {
      setAnalyzeCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(analyzeKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setAnalyzeCountdown(remaining);
      } else {
        localStorage.removeItem(analyzeKey);
        setAnalyzeCountdown(0);
      }
    } else {
      setAnalyzeCountdown(0);
    }
  }, [currentProject?.url, url]);

  // Check download countdown on mount and when project/url changes
  useEffect(() => {
    const targetUrl = currentProject?.url || url || null;
    if (!targetUrl) {
      setDownloadCountdown(0);
      return;
    }

    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) {
      setDownloadCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(downloadKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setDownloadCountdown(remaining);
      } else {
        localStorage.removeItem(downloadKey);
        setDownloadCountdown(0);
      }
    } else {
      setDownloadCountdown(0);
    }
  }, [currentProject?.url, url]);

  // Analyze countdown timer effect
  useEffect(() => {
    if (analyzeCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || url || null;
    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) return;

    const timer = setInterval(() => {
      setAnalyzeCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(analyzeKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [analyzeCountdown, currentProject?.url, url]);

  // Download countdown timer effect
  useEffect(() => {
    if (downloadCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || url || null;
    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) return;

    const timer = setInterval(() => {
      setDownloadCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(downloadKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [downloadCountdown, currentProject?.url, url]);

  // Function to update current view based on selected device
  const updateCurrentView = () => {
    if (viewDevice === 'desktop' && desktopResults) {
      setResults(desktopResults);
    } else if (viewDevice === 'mobile' && mobileResults) {
      setResults(mobileResults);
    } else {
      // Show whichever is available, or null if neither
      setResults(desktopResults || mobileResults || null);
    }
  };

  // Load cached results when URL changes
  useEffect(() => {
    if (!url) {
      setDesktopResults(null);
      setMobileResults(null);
      setResults(null);
      return;
    }

    // Load both desktop and mobile cached results
    const desktopCacheKey = getCacheKey(url, 'desktop');
    const mobileCacheKey = getCacheKey(url, 'mobile');

    let loadedDesktop = null;
    let loadedMobile = null;

    // Load desktop cache
    if (desktopCacheKey) {
      try {
        const cached = localStorage.getItem(desktopCacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const cacheTimestamp = parsed.cachedAt || parsed.timestamp;
          if (cacheTimestamp) {
            const savedTime = new Date(cacheTimestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            if (hoursDiff < 24) {
              loadedDesktop = parsed;
            } else {
              localStorage.removeItem(desktopCacheKey);
            }
          }
        }
      } catch (err) {
        console.error('Error loading desktop cache:', err);
        if (desktopCacheKey) localStorage.removeItem(desktopCacheKey);
      }
    }

    // Load mobile cache
    if (mobileCacheKey) {
      try {
        const cached = localStorage.getItem(mobileCacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const cacheTimestamp = parsed.cachedAt || parsed.timestamp;
          if (cacheTimestamp) {
            const savedTime = new Date(cacheTimestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            if (hoursDiff < 24) {
              loadedMobile = parsed;
            } else {
              localStorage.removeItem(mobileCacheKey);
            }
          }
        }
      } catch (err) {
        console.error('Error loading mobile cache:', err);
        if (mobileCacheKey) localStorage.removeItem(mobileCacheKey);
      }
    }

    // Update state with loaded results
    setDesktopResults(loadedDesktop);
    setMobileResults(loadedMobile);
    
    // Set current view based on available results
    if (viewDevice === 'desktop' && loadedDesktop) {
      setResults(loadedDesktop);
    } else if (viewDevice === 'mobile' && loadedMobile) {
      setResults(loadedMobile);
    } else {
      setResults(loadedDesktop || loadedMobile || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Update current view results when desktop/mobile results or view device changes
  useEffect(() => {
    updateCurrentView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopResults, mobileResults, viewDevice]);

  const handleAnalyze = async (e) => {
    if (e) {
      e.preventDefault();
    }

    // Prevent analyze during countdown
    if (analyzeCountdown > 0) return;

    const targetUrl = url;
    if (!targetUrl || !targetUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Validate that the URL belongs to the currently selected project
    if (!currentProject || !currentProject.url) {
      setError('Please select a project first.');
      return;
    }

    // Format and validate URL
    let formattedUrl = targetUrl.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // Validate URL format
    let urlObj;
    try {
      urlObj = new URL(formattedUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return;
    }

    const targetDomain = urlObj.hostname.toLowerCase();
    const targetOrigin = urlObj.origin.toLowerCase();

    // Normalize project URL (add protocol if missing)
    let projectUrl = currentProject.url.trim();
    if (!projectUrl.startsWith('http://') && !projectUrl.startsWith('https://')) {
      projectUrl = 'https://' + projectUrl;
    }

    let belongsToProject = false;
    try {
      const projectUrlObj = new URL(projectUrl);
      const projectDomain = projectUrlObj.hostname.toLowerCase();
      const projectOrigin = projectUrlObj.origin.toLowerCase();
      
      // Check if domains match (allows subdomains)
      // For example: www.example.com matches example.com
      // But example.com does NOT match other-example.com
      belongsToProject = targetDomain === projectDomain || 
             targetDomain.endsWith('.' + projectDomain) ||
             projectDomain.endsWith('.' + targetDomain) ||
             targetOrigin === projectOrigin;
    } catch (e) {
      setError('Invalid project URL format.');
      return;
    }

    if (!belongsToProject) {
      setError(`The URL must belong to the selected project domain (${currentProject.url}). Please use a URL from this project.`);
      return;
    }

    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Analyzing Core Web Vitals...');
    setShowLoadingScreen(true);
    setLoading(true);
    setError(null);
    // Keep showing previous results while loading new ones

    try {
      // Analyze both desktop and mobile simultaneously
      const [desktopResponse, mobileResponse] = await Promise.all([
        axios.post(getApiUrl('core-web-vitals'), { 
          url: formattedUrl,
          strategy: 'desktop'
        }, axiosConfig),
        axios.post(getApiUrl('core-web-vitals'), { 
          url: formattedUrl,
          strategy: 'mobile'
        }, axiosConfig)
      ]);

      const desktopData = desktopResponse.data;
      const mobileData = mobileResponse.data;
      
      // Debug logging - show actual values
      console.log('=== Desktop Category Scores ===');
      console.log('Performance:', desktopData.categoryScores?.performance);
      console.log('Accessibility:', desktopData.categoryScores?.accessibility);
      console.log('Best Practices:', desktopData.categoryScores?.bestPractices);
      console.log('SEO:', desktopData.categoryScores?.seo);
      console.log('Full Desktop categoryScores:', JSON.stringify(desktopData.categoryScores, null, 2));
      
      console.log('=== Mobile Category Scores ===');
      console.log('Performance:', mobileData.categoryScores?.performance);
      console.log('Accessibility:', mobileData.categoryScores?.accessibility);
      console.log('Best Practices:', mobileData.categoryScores?.bestPractices);
      console.log('SEO:', mobileData.categoryScores?.seo);
      console.log('Full Mobile categoryScores:', JSON.stringify(mobileData.categoryScores, null, 2));
      
      // Add timestamp for cache validation
      desktopData.cachedAt = new Date().toISOString();
      mobileData.cachedAt = new Date().toISOString();
      
      setDesktopResults(desktopData);
      setMobileResults(mobileData);
      
      // Cache both results in localStorage
      const desktopCacheKey = getCacheKey(formattedUrl, 'desktop');
      const mobileCacheKey = getCacheKey(formattedUrl, 'mobile');
      
      if (desktopCacheKey) {
        try {
          localStorage.setItem(desktopCacheKey, JSON.stringify(desktopData));
        } catch (err) {
          console.error('Error caching desktop results:', err);
        }
      }
      
      if (mobileCacheKey) {
        try {
          localStorage.setItem(mobileCacheKey, JSON.stringify(mobileData));
        } catch (err) {
          console.error('Error caching mobile results:', err);
        }
      }
      
      // Update current view
      updateCurrentView();
      
      // Save results to project if we have a current project
      if (currentProject) {
        updateProject(currentProject.id, {
          coreWebVitals: {
            desktop: desktopData,
            mobile: mobileData
          },
          lastVitalsDate: new Date().toISOString()
        });
      }

      // Start 2-minute countdown (120 seconds) for analyze action
      const analyzeKey = getAnalyzeCountdownKey(targetUrl);
      if (analyzeKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(analyzeKey, countdownEndTime.toString());
        setAnalyzeCountdown(120);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze Core Web Vitals. Please try again.');
      // Don't clear results on error - keep showing cached results if available
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  const handleDeviceChange = (newDevice) => {
    if (newDevice === viewDevice) return; // No change needed
    setViewDevice(newDevice);
    // Update current view - no re-analysis needed, just switch view
    updateCurrentView();
  };

  const handleReAnalyze = () => {
    if (currentProject) {
      setUrl(currentProject.url);
      // Small delay to ensure URL is set, then analyze
      setTimeout(() => {
        handleAnalyze(null);
      }, 100);
    } else if (url) {
      handleAnalyze(null);
    }
  };

  const handleDownloadReport = async () => {
    if ((!desktopResults && !mobileResults) || !reportRef.current) return;

    // Prevent download during countdown
    if (downloadCountdown > 0) return;
    
    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Generating report...');
    setShowLoadingScreen(true);
    
    // Force light theme for the printed report
    const prevThemeAttr = document.documentElement.getAttribute('data-theme') || 'light';
    if (prevThemeAttr !== 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }

    // Wait a moment for theme to apply
    await new Promise(resolve => setTimeout(resolve, 100));

    // Load custom report settings from localStorage
    let reportSettings = {
      logoUrl: '/logo.png',
      leftFooterText: 'Generated by Parrot SEO',
      rightFooterText: 'parrotseo.com',
      watermarkEnabled: true
    };
    
    try {
      const savedSettings = localStorage.getItem('reportSettings');
      const normalizedPlan = (userPlan || 'basic').toLowerCase();
      const isBasicPlan = !normalizedPlan || normalizedPlan === 'basic';

      if (!isBasicPlan && savedSettings) {
        // Only apply custom branding for non-basic plans
        reportSettings = { ...reportSettings, ...JSON.parse(savedSettings) };
      } else if (isBasicPlan) {
        // Force defaults for Basic plan users to avoid using any old custom settings
        reportSettings = {
          logoUrl: '/logo.png',
          leftFooterText: 'Generated by Parrot SEO',
          rightFooterText: 'parrotseo.com',
          watermarkEnabled: true
        };
      }
    } catch (error) {
      console.warn('Error loading report settings:', error);
    }

    try {
      // Create new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set margins
      const margin = 15;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = margin;
      const headerStartY = margin;

      // Helper to add header logo on right side
      const addHeaderLogo = async () => {
        try {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.src = reportSettings.logoUrl || '/logo.png';
          
          await new Promise((resolve) => {
            logoImg.onload = () => {
              try {
                const logoHeight = 20;
                const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
                const logoX = pageWidth - margin - logoWidth;
                pdf.addImage(logoImg, 'PNG', logoX, headerStartY, logoWidth, logoHeight);
              } catch (error) {
                console.warn('Could not add logo to PDF:', error);
              }
              resolve();
            };
            logoImg.onerror = () => resolve();
            setTimeout(() => resolve(), 1000);
          });
        } catch (error) {
          console.warn('Error loading logo:', error);
        }
      };

      // Helper to add footer to a specific page
      const addFooterToPage = (pageNumber) => {
        pdf.setPage(pageNumber);
        const footerY = pageHeight - margin + 5;
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(reportSettings.leftFooterText || 'Generated by Parrot SEO', margin, footerY);
        const rightText = reportSettings.rightFooterText || 'parrotseo.com';
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, footerY);
      };

      // Helper to add footer to current page
      const addFooter = () => {
        const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
        addFooterToPage(currentPage);
      };

      // Helper to check if new page needed
      const checkNewPage = (requiredHeight = 10) => {
        const footerSpace = 10; // Space reserved for footer
        if (yPosition + requiredHeight > pageHeight - margin - footerSpace) {
          addFooter();
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper to render a table with proper text wrapping and row pagination
      const renderTable = (headers, rows, startY) => {
        let currentY = startY;
        const tableWidth = pageWidth - (margin * 2);
        const colWidths = headers.map(() => tableWidth / headers.length);
        const baseRowHeight = 8;
        const footerSpace = 10; // Space reserved for footer
        
        // Check if we have space for header before drawing
        checkNewPage(baseRowHeight);
        
        // Table header
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, currentY, tableWidth, baseRowHeight, 'F');
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        
        let xPos = margin;
        headers.forEach((header, idx) => {
          pdf.text(header, xPos + 2, currentY + 5, { maxWidth: colWidths[idx] - 4 });
          xPos += colWidths[idx];
        });
        
        currentY += baseRowHeight;
        
        // Table rows
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        rows.forEach((row, rowIdx) => {
          // Calculate required height for this row based on text wrapping
          let maxLines = 1;
          row.forEach((cell, colIdx) => {
            const cellText = String(cell || 'N/A');
            const lines = pdf.splitTextToSize(cellText, colWidths[colIdx] - 4);
            maxLines = Math.max(maxLines, lines.length);
          });
          
          const rowHeight = Math.max(baseRowHeight, maxLines * 4 + 2); // 4mm per line + padding
          
          // Check if row fits on current page BEFORE drawing it
          // If not, move to next page and redraw header
          if (currentY + rowHeight > pageHeight - margin - footerSpace) {
            // Add footer to current page before moving
            addFooter();
            pdf.addPage();
            currentY = margin;
            
            // Redraw header on new page
            pdf.setFillColor(240, 240, 240);
            pdf.rect(margin, currentY, tableWidth, baseRowHeight, 'F');
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            xPos = margin;
            headers.forEach((header, idx) => {
              pdf.text(header, xPos + 2, currentY + 5, { maxWidth: colWidths[idx] - 4 });
              xPos += colWidths[idx];
            });
            currentY += baseRowHeight;
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
          }
          
          // Draw row border
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(margin, currentY, tableWidth, rowHeight, 'S');
          
          xPos = margin;
          row.forEach((cell, colIdx) => {
            const cellText = String(cell || 'N/A');
            const lines = pdf.splitTextToSize(cellText, colWidths[colIdx] - 4);
            const cellY = currentY + 4; // Start text slightly below top border
            lines.forEach((line, lineIdx) => {
              pdf.text(line, xPos + 2, cellY + (lineIdx * 4));
            });
            xPos += colWidths[colIdx];
          });
          
          currentY += rowHeight;
        });
        
        return currentY;
      };

      // Add header logo
      await addHeaderLogo();

      // Add title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Core Web Vitals Report', margin, yPosition);
      yPosition += 12; // Increased spacing after main title

      // Add URL and date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const url = desktopResults?.url || mobileResults?.url || '';
      pdf.text(`URL: ${url}`, margin, yPosition);
      yPosition += 7; // Increased spacing
      const timestamp = desktopResults?.timestamp || mobileResults?.timestamp;
      if (timestamp) {
        pdf.text(`Analyzed: ${new Date(timestamp).toLocaleString()}`, margin, yPosition);
        yPosition += 7; // Increased spacing
      }
      yPosition += 10; // Extra spacing before first section

      // Lighthouse Scores Tables (Desktop & Mobile)
      checkNewPage(40);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Lighthouse Scores', margin, yPosition);
      yPosition += 8; // Spacing after main section title

      // Helper function to get score remark
      const getScoreRemark = (score, categoryName) => {
        if (score === null || score === undefined) {
          return 'No data available for this category.';
        }
        if (score >= 90) {
          return `Excellent ${categoryName} score. Your website performs very well in this category.`;
        } else if (score >= 50) {
          return `Good ${categoryName} score, but there's room for improvement. Consider optimizing to reach 90+.`;
        } else {
          return `Poor ${categoryName} score. Significant improvements needed to enhance user experience.`;
        }
      };

      // Desktop Lighthouse Scores Table
      if (desktopResults?.categoryScores) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Desktop', margin, yPosition);
        yPosition += 6;

        const desktopHeaders = ['Category', 'Score', 'Remarks'];
        const desktopRows = [
          ['Performance',
            desktopResults.categoryScores.performance !== null && desktopResults.categoryScores.performance !== undefined 
              ? `${desktopResults.categoryScores.performance}/100` : 'N/A',
            getScoreRemark(desktopResults.categoryScores.performance, 'Performance')
          ],
          ['Accessibility',
            desktopResults.categoryScores.accessibility !== null && desktopResults.categoryScores.accessibility !== undefined 
              ? `${desktopResults.categoryScores.accessibility}/100` : 'N/A',
            getScoreRemark(desktopResults.categoryScores.accessibility, 'Accessibility')
          ],
          ['Best Practices',
            desktopResults.categoryScores.bestPractices !== null && desktopResults.categoryScores.bestPractices !== undefined 
              ? `${desktopResults.categoryScores.bestPractices}/100` : 'N/A',
            getScoreRemark(desktopResults.categoryScores.bestPractices, 'Best Practices')
          ],
          ['SEO',
            desktopResults.categoryScores.seo !== null && desktopResults.categoryScores.seo !== undefined 
              ? `${desktopResults.categoryScores.seo}/100` : 'N/A',
            getScoreRemark(desktopResults.categoryScores.seo, 'SEO')
          ]
        ];
        
        yPosition = renderTable(desktopHeaders, desktopRows, yPosition);
        yPosition += 8; // Spacing after desktop table
      }

      // Mobile Lighthouse Scores Table
      if (mobileResults?.categoryScores) {
        checkNewPage(40);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Mobile', margin, yPosition);
        yPosition += 6;

        const mobileHeaders = ['Category', 'Score', 'Remarks'];
        const mobileRows = [
          ['Performance',
            mobileResults.categoryScores.performance !== null && mobileResults.categoryScores.performance !== undefined 
              ? `${mobileResults.categoryScores.performance}/100` : 'N/A',
            getScoreRemark(mobileResults.categoryScores.performance, 'Performance')
          ],
          ['Accessibility',
            mobileResults.categoryScores.accessibility !== null && mobileResults.categoryScores.accessibility !== undefined 
              ? `${mobileResults.categoryScores.accessibility}/100` : 'N/A',
            getScoreRemark(mobileResults.categoryScores.accessibility, 'Accessibility')
          ],
          ['Best Practices',
            mobileResults.categoryScores.bestPractices !== null && mobileResults.categoryScores.bestPractices !== undefined 
              ? `${mobileResults.categoryScores.bestPractices}/100` : 'N/A',
            getScoreRemark(mobileResults.categoryScores.bestPractices, 'Best Practices')
          ],
          ['SEO',
            mobileResults.categoryScores.seo !== null && mobileResults.categoryScores.seo !== undefined 
              ? `${mobileResults.categoryScores.seo}/100` : 'N/A',
            getScoreRemark(mobileResults.categoryScores.seo, 'SEO')
          ]
        ];
        
        yPosition = renderTable(mobileHeaders, mobileRows, yPosition);
        yPosition += 10; // Increased spacing after mobile table
      }

      // Performance Metrics Tables (Desktop & Mobile)
      checkNewPage(50);
      yPosition += 5; // Extra spacing before section
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Performance Metrics', margin, yPosition);
      yPosition += 8; // Spacing after main section title

      const getMetricValue = (metric, unit = '') => {
        if (!metric) return 'N/A';
        if (metric.displayValue) return metric.displayValue;
        return formatMetric(metric, unit);
      };

      // Desktop metrics table
      if (desktopResults) {
        checkNewPage(40);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Desktop', margin, yPosition);
        yPosition += 6;

        const desktopHeaders = ['Metric', 'Value', 'Remarks'];
        const desktopRows = [
          [
            'First Contentful Paint (FCP)',
            getMetricValue(desktopResults.firstContentfulPaint, 's'),
            getMetricRemark('FCP', desktopResults.firstContentfulPaint, 's')
          ],
          [
            'Largest Contentful Paint (LCP)',
            getMetricValue(desktopResults.lcp, 's'),
            getMetricRemark('LCP', desktopResults.lcp, 's')
          ],
          [
            'Total Blocking Time (TBT)',
            getMetricValue(desktopResults.totalBlockingTime, 'ms'),
            getMetricRemark('TBT', desktopResults.totalBlockingTime, 'ms')
          ],
          [
            'Cumulative Layout Shift (CLS)',
            getMetricValue(desktopResults.cls, ''),
            getMetricRemark('CLS', desktopResults.cls, '')
          ],
          [
            'Speed Index',
            getMetricValue(desktopResults.speedIndex, 's'),
            getMetricRemark('Speed Index', desktopResults.speedIndex, 's')
          ]
        ];

        yPosition = renderTable(desktopHeaders, desktopRows, yPosition);
        yPosition += 8; // Spacing after desktop table
      }

      // Mobile metrics table
      if (mobileResults) {
        checkNewPage(40);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Mobile', margin, yPosition);
        yPosition += 6;

        const mobileHeaders = ['Metric', 'Value', 'Remarks'];
        const mobileRows = [
          [
            'First Contentful Paint (FCP)',
            getMetricValue(mobileResults.firstContentfulPaint, 's'),
            getMetricRemark('FCP', mobileResults.firstContentfulPaint, 's')
          ],
          [
            'Largest Contentful Paint (LCP)',
            getMetricValue(mobileResults.lcp, 's'),
            getMetricRemark('LCP', mobileResults.lcp, 's')
          ],
          [
            'Total Blocking Time (TBT)',
            getMetricValue(mobileResults.totalBlockingTime, 'ms'),
            getMetricRemark('TBT', mobileResults.totalBlockingTime, 'ms')
          ],
          [
            'Cumulative Layout Shift (CLS)',
            getMetricValue(mobileResults.cls, ''),
            getMetricRemark('CLS', mobileResults.cls, '')
          ],
          [
            'Speed Index',
            getMetricValue(mobileResults.speedIndex, 's'),
            getMetricRemark('Speed Index', mobileResults.speedIndex, 's')
          ]
        ];

        yPosition = renderTable(mobileHeaders, mobileRows, yPosition);
      }

      // Add footer and watermark to all pages
      let logoImageData = null;
      
      // Try to load logo for watermark (only if enabled)
      if (reportSettings.watermarkEnabled) {
        try {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.src = reportSettings.logoUrl || '/logo.png';
          
          logoImageData = await new Promise((resolve) => {
            logoImg.onload = () => {
              try {
                // Create a canvas to make the logo semi-transparent
                const canvas = document.createElement('canvas');
                canvas.width = logoImg.width;
                canvas.height = logoImg.height;
                const ctx = canvas.getContext('2d');
                
                // Set opacity for watermark effect (0.1 = 10% opacity)
                ctx.globalAlpha = 0.1;
                ctx.drawImage(logoImg, 0, 0);
                
                // Convert to data URL
                const imageData = canvas.toDataURL('image/png');
                resolve(imageData);
              } catch (error) {
                console.warn('Could not create watermark logo:', error);
                resolve(null);
              }
            };
            logoImg.onerror = () => {
              console.warn('Could not load logo for watermark');
              resolve(null);
            };
            setTimeout(() => resolve(null), 2000);
          });
        } catch (error) {
          console.warn('Error loading logo for watermark:', error);
        }
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Add footer
        addFooterToPage(i);
        
        // Add watermark logo in center if available and enabled
        if (logoImageData && reportSettings.watermarkEnabled) {
          try {
            const logoImg = new Image();
            logoImg.src = reportSettings.logoUrl || '/logo.png';
            
            await new Promise((resolve) => {
              logoImg.onload = () => {
                try {
                  // Calculate center position
                  const watermarkWidth = 80; // Width in mm
                  const watermarkHeight = (logoImg.height / logoImg.width) * watermarkWidth;
                  const centerX = (pageWidth - watermarkWidth) / 2;
                  const centerY = (pageHeight - watermarkHeight) / 2;
                  
                  // Add watermark logo in center (already has transparency from canvas)
                  pdf.addImage(logoImageData, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight);
                } catch (error) {
                  console.warn('Could not add watermark logo:', error);
                }
                resolve();
              };
              logoImg.onerror = () => resolve();
              setTimeout(() => resolve(), 500);
            });
          } catch (error) {
            console.warn('Error adding watermark logo:', error);
          }
        }
      }

      // Restore theme
      if (prevThemeAttr !== 'light') {
        document.documentElement.setAttribute('data-theme', prevThemeAttr);
      }

      // Generate filename
      const urlSlug = url.replace(/^https?:\/\//, '').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Core_Web_Vitals_${urlSlug}_${dateStr}.pdf`;

      // Save PDF
      pdf.save(filename);

      // Start 2-minute countdown (120 seconds) after successful download
      const targetUrl = currentProject?.url || url;
      const downloadKey = getDownloadCountdownKey(targetUrl);
      if (downloadKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(downloadKey, countdownEndTime.toString());
        setDownloadCountdown(120);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
      
      // Restore theme
      if (prevThemeAttr !== 'light') {
        document.documentElement.setAttribute('data-theme', prevThemeAttr);
      }
    } finally {
      setShowLoadingScreen(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#22c55e'; // green
    if (score >= 50) return '#eab308'; // yellow
    return '#ef4444'; // red
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Good';
    if (score >= 50) return 'Needs Improvement';
    return 'Poor';
  };

  const formatMetric = (value, unit = '') => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object' && value.value !== undefined) {
      // Handle metric object with value property
      const numValue = typeof value.value === 'string' ? parseFloat(value.value) : value.value;
      if (isNaN(numValue)) return 'N/A';
      return `${numValue.toFixed(unit === '' ? 3 : 2)}${unit}`;
    }
    if (typeof value === 'number') {
      return `${value.toFixed(unit === '' ? 3 : 2)}${unit}`;
    }
    return value;
  };

  // Format bytes to human-readable format
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KiB', 'MiB', 'GiB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format milliseconds to human-readable format
  const formatTime = (ms) => {
    if (!ms || ms === 0) return '0 ms';
    if (ms < 1000) return Math.round(ms) + ' ms';
    return (ms / 1000).toFixed(2) + ' s';
  };

  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  // Toggle insight expansion
  const toggleInsight = (insightId) => {
    const newExpanded = new Set(expandedInsights);
    if (newExpanded.has(insightId)) {
      newExpanded.delete(insightId);
    } else {
      newExpanded.add(insightId);
    }
    setExpandedInsights(newExpanded);
  };

  // Get estimated savings text for insight header
  const getInsightSavingsText = (insight) => {
    if (!insight.details) return null;
    const details = insight.details;
    if (details.overallSavingsBytes > 0) {
      return `Est savings of ${formatBytes(details.overallSavingsBytes)}`;
    }
    if (details.overallSavingsMs > 0) {
      return `Est savings of ${formatTime(details.overallSavingsMs)}`;
    }
    if (insight.displayValue) {
      return insight.displayValue;
    }
    return null;
  };

  // Render network dependency tree
  const renderNetworkDependencyTree = (insight) => {
    if (!insight.details) return null;
    
    const details = insight.details;
    // Lighthouse critical request chains structure
    const chains = details.chains || {};
    const longestChain = details.longestChain || {};
    const maxLatency = longestChain.duration || 0;
    
    const renderChain = (chain, level = 0, parentKey = '') => {
      if (!chain) return null;
      
      const request = chain.request || {};
      const children = chain.children || [];
      const url = request.url || '';
      const duration = request.duration || 0;
      const transferSize = request.transferSize || 0;
      const isMaxLatency = duration === maxLatency && maxLatency > 0;
      const uniqueKey = `${parentKey}-${url}-${level}`;
      
      return (
        <div key={uniqueKey} className="dependency-chain-item">
          <div className={`dependency-resource ${isMaxLatency ? 'max-latency' : ''}`}>
            <div className="dependency-resource-main">
              <div className="dependency-resource-info">
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="dependency-url"
                  title={url}
                >
                  {url ? (url.length > 60 ? url.substring(0, 60) + '...' : url) : 'Unattributable'}
                </a>
                <div className="dependency-metrics">
                  <span className="dependency-duration">{formatTime(duration)}</span>
                  {transferSize > 0 && (
                    <span className="dependency-size">{formatBytes(transferSize)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {children.length > 0 && (
            <div className="dependency-children">
              {children.map((child, idx) => renderChain(child, level + 1, uniqueKey))}
            </div>
          )}
        </div>
      );
    };
    
    // Handle both object format (chains: { chainId: chain }) and array format
    let chainEntries = [];
    if (Array.isArray(chains)) {
      chainEntries = chains;
    } else if (typeof chains === 'object') {
      chainEntries = Object.values(chains);
    }
    
    if (chainEntries.length === 0) return null;
    
    return (
      <div className="network-dependency-tree">
        {maxLatency > 0 && (
          <div className="dependency-max-latency">
            <strong>Maximum critical path latency:</strong> {formatTime(maxLatency)}
          </div>
        )}
        <div className="dependency-tree-container">
          {chainEntries.map((chain, idx) => (
            <div key={idx} className="dependency-chain">
              {renderChain(chain)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render insight details (tables, savings, etc.)
  const renderInsightDetails = (insight) => {
    if (!insight.details) return null;

    const details = insight.details;
    const isUnusedResource = insight.id === 'unused-css-rules' || insight.id === 'unused-javascript';
    
    // For unused CSS/JS, check if we have items even if type is not 'table'
    const hasTable = (details.type === 'table' && details.items && details.items.length > 0) ||
                     (isUnusedResource && details.items && details.items.length > 0);
    const hasSavings = details.overallSavingsMs > 0 || details.overallSavingsBytes > 0;
    const isNetworkTree = details.type === 'criticalrequestchain';

    // Debug logging for unused resources
    if (isUnusedResource) {
      console.log('=== Unused Resource Debug ===');
      console.log('Insight ID:', insight.id);
      console.log('Has Table:', hasTable);
      console.log('Details type:', details.type);
      console.log('Details:', JSON.stringify(details, null, 2));
      console.log('Items:', details.items);
      if (details.items && details.items.length > 0) {
        console.log('First Item:', details.items[0]);
        console.log('First Item keys:', Object.keys(details.items[0]));
        if (details.items[0].subItems) {
          console.log('First Item subItems:', details.items[0].subItems);
        }
      }
      console.log('Headings:', details.headings);
    }

    // Render network dependency tree
    if (isNetworkTree) {
      return (
        <div className="insight-details">
          {renderNetworkDependencyTree(insight)}
        </div>
      );
    }

    return (
      <div className="insight-details">
        {/* Estimated Savings */}
        {hasSavings && (
          <div className="insight-savings">
            {details.overallSavingsBytes > 0 && (
              <span className="savings-badge">
                Estimated savings: {formatBytes(details.overallSavingsBytes)}
              </span>
            )}
            {details.overallSavingsMs > 0 && (
              <span className="savings-badge">
                Time savings: {formatTime(details.overallSavingsMs)}
              </span>
            )}
          </div>
        )}

        {/* Table with resource details */}
        {hasTable && (() => {
          let tableItems = details.items || [];
          let tableHeadings = details.headings || [];
          
          // For unused CSS/JS, we need to expand subItems into separate rows
          if (isUnusedResource && tableItems.length > 0) {
            // Check if items have subItems
            const hasSubItems = tableItems.some(item => item.subItems && item.subItems.items && item.subItems.items.length > 0);
            
            if (hasSubItems) {
              // Flatten subItems into individual rows
              const expandedItems = [];
              tableItems.forEach(item => {
                if (item.subItems && item.subItems.items && item.subItems.items.length > 0) {
                  item.subItems.items.forEach(subItem => {
                    expandedItems.push({
                      url: subItem.url || item.url || '-',
                      totalBytes: subItem.totalBytes || subItem.wastedBytes || 0,
                      wastedBytes: subItem.wastedBytes || subItem.totalBytes || 0,
                      ...subItem
                    });
                  });
                } else {
                  // If no subItems, use the item itself
                  expandedItems.push(item);
                }
              });
              tableItems = expandedItems;
              
              // Use subItemsHeading for unused CSS/JS if available
              if (tableHeadings.length > 0 && tableHeadings[0].subItemsHeading) {
                tableHeadings = tableHeadings.map(heading => {
                  const subHeading = heading.subItemsHeading || {};
                  return {
                    key: subHeading.key || heading.key || '',
                    valueType: subHeading.valueType || heading.valueType || '',
                    label: subHeading.label || heading.label || heading.key || '',
                    text: subHeading.text || heading.text || ''
                  };
                });
              }
            } else {
              // No subItems, use items directly but ensure we have proper headings
              // For unused CSS/JS, try to extract data from items directly
              if (tableHeadings.length === 0 && tableItems.length > 0) {
                // Create headings from first item keys
                const firstItem = tableItems[0];
                tableHeadings = Object.keys(firstItem).map(key => ({
                  key: key,
                  label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                  valueType: key
                }));
              }
            }
          }
          
          // If no headings, create default ones
          if (tableHeadings.length === 0 && tableItems.length > 0) {
            tableHeadings = [
              { key: 'url', label: 'URL', valueType: 'url' },
              { key: 'totalBytes', label: 'Transfer Size', valueType: 'bytes' },
              { key: 'wastedBytes', label: 'Wasted Bytes', valueType: 'bytes' }
            ];
          }
          
          return (
            <div className="insight-table-container">
              <table className="insight-details-table">
                <thead>
                  <tr>
                    {tableHeadings.map((heading, idx) => (
                      <th key={idx}>{heading.label || heading.text || heading.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableItems.map((item, idx) => (
                    <tr key={idx}>
                      {tableHeadings.map((heading, hIdx) => {
                        const key = heading.key || heading.valueType || '';
                        let cellValue = item[key] || item[heading.label] || item[heading.text] || '-';
                      
                      // Handle object values
                      if (typeof cellValue === 'object' && cellValue !== null) {
                        // If it's a URL object, extract the URL
                        if (cellValue.url) {
                          const url = cellValue.url;
                          const label = cellValue.label || cellValue.text || url;
                          return (
                            <td key={hIdx} className="insight-url-cell">
                              {url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                                  {label || url}
                                </a>
                              ) : (
                                <span>{label || 'Unattributable'}</span>
                              )}
                            </td>
                          );
                        }
                        // If it's a node object with snippet or label
                        if (cellValue.snippet || cellValue.nodeLabel || cellValue.text) {
                          cellValue = cellValue.snippet || cellValue.nodeLabel || cellValue.text || '-';
                        } else {
                          // For other objects, try to stringify or show a placeholder
                          cellValue = cellValue.type || cellValue.label || JSON.stringify(cellValue).substring(0, 50) || '-';
                        }
                      }
                      
                      // Format specific value types
                      if (key === 'url' || key === 'source') {
                        const url = typeof cellValue === 'string' ? cellValue : '';
                        return (
                          <td key={hIdx} className="insight-url-cell">
                            {url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                                {url}
                              </a>
                            ) : (
                              <span>{cellValue || 'Unattributable'}</span>
                            )}
                          </td>
                        );
                      }
                      
                      // Format numeric values
                      if (typeof cellValue === 'number') {
                        if (key === 'totalBytes' || key === 'wastedBytes' || key === 'transferSize' || key === 'size' || key === 'transferSize') {
                          cellValue = formatBytes(cellValue);
                        } else if (key === 'wastedMs' || key === 'duration' || key === 'time') {
                          cellValue = formatTime(cellValue);
                        }
                      }
                      
                      // Ensure we're rendering a string or number
                      if (typeof cellValue !== 'string' && typeof cellValue !== 'number') {
                        cellValue = String(cellValue || '-');
                      }
                      
                      return <td key={hIdx}>{cellValue}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>
    );
  };

  // Get color for metric value based on thresholds
  const getMetricValueColor = (metricName, metric, unit = '') => {
    if (!metric) {
      return 'var(--text-primary)';
    }

    // Handle different metric formats
    let value = null;
    if (metric.value !== null && metric.value !== undefined) {
      value = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
    } else if (metric.displayValue) {
      // Try to extract numeric value from displayValue (e.g., "1.2s" -> 1.2)
      const displayStr = metric.displayValue.toString();
      const match = displayStr.match(/[\d.]+/);
      if (match) {
        value = parseFloat(match[0]);
      }
    }

    if (value === null || isNaN(value)) {
      return 'var(--text-primary)';
    }

    // Determine color based on metric thresholds
    switch (metricName) {
      case 'FCP':
        if (value < 1.8) return '#22c55e'; // green
        if (value < 3.0) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'LCP':
        if (value < 2.5) return '#22c55e'; // green
        if (value < 4.0) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'FID':
        if (value < 100) return '#22c55e'; // green
        if (value < 300) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'INP':
        if (value < 200) return '#22c55e'; // green
        if (value < 500) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'CLS':
        if (value < 0.1) return '#22c55e'; // green
        if (value < 0.25) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'TBT':
        if (value < 200) return '#22c55e'; // green
        if (value < 600) return '#f59e0b'; // orange
        return '#ef4444'; // red
      case 'Speed Index':
        if (value < 3.4) return '#22c55e'; // green
        if (value < 5.8) return '#f59e0b'; // orange
        return '#ef4444'; // red
      default:
        return 'var(--text-primary)';
    }
  };

  // Get threshold ranges for each metric as table data
  const getMetricThresholds = (metricName) => {
    switch (metricName) {
      case 'FCP':
        return [
          { label: 'Good', range: '< 1.8s', color: '#22c55e' },
          { label: 'Needs Improvement', range: '1.8s - 3.0s', color: '#f59e0b' },
          { label: 'Poor', range: '> 3.0s', color: '#ef4444' }
        ];
      case 'LCP':
        return [
          { label: 'Good', range: '< 2.5s', color: '#22c55e' },
          { label: 'Needs Improvement', range: '2.5s - 4.0s', color: '#f59e0b' },
          { label: 'Poor', range: '> 4.0s', color: '#ef4444' }
        ];
      case 'FID':
        return [
          { label: 'Good', range: '< 100ms', color: '#22c55e' },
          { label: 'Needs Improvement', range: '100ms - 300ms', color: '#f59e0b' },
          { label: 'Poor', range: '> 300ms', color: '#ef4444' }
        ];
      case 'INP':
        return [
          { label: 'Good', range: '< 200ms', color: '#22c55e' },
          { label: 'Needs Improvement', range: '200ms - 500ms', color: '#f59e0b' },
          { label: 'Poor', range: '> 500ms', color: '#ef4444' }
        ];
      case 'CLS':
        return [
          { label: 'Good', range: '< 0.1', color: '#22c55e' },
          { label: 'Needs Improvement', range: '0.1 - 0.25', color: '#f59e0b' },
          { label: 'Poor', range: '> 0.25', color: '#ef4444' }
        ];
      case 'TBT':
        return [
          { label: 'Good', range: '< 200ms', color: '#22c55e' },
          { label: 'Needs Improvement', range: '200ms - 600ms', color: '#f59e0b' },
          { label: 'Poor', range: '> 600ms', color: '#ef4444' }
        ];
      case 'Speed Index':
        return [
          { label: 'Good', range: '< 3.4s', color: '#22c55e' },
          { label: 'Needs Improvement', range: '3.4s - 5.8s', color: '#f59e0b' },
          { label: 'Poor', range: '> 5.8s', color: '#ef4444' }
        ];
      default:
        return [];
    }
  };

  // Generate remarks/feedback for each metric
  const getMetricRemark = (metricName, metric, unit = '') => {
    if (!metric) {
      return 'No data available for this metric.';
    }

    // Handle different metric formats
    let value = null;
    if (metric.value !== null && metric.value !== undefined) {
      value = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
    } else if (metric.displayValue) {
      // Try to extract numeric value from displayValue (e.g., "1.2s" -> 1.2)
      const displayStr = metric.displayValue.toString();
      const match = displayStr.match(/[\d.]+/);
      if (match) {
        value = parseFloat(match[0]);
      }
    }

    if (value === null || isNaN(value)) {
      return 'No data available for this metric.';
    }

    const score = metric.score || 0;

    switch (metricName) {
      case 'FCP':
        if (value < 1.8) {
          return 'Excellent! Your First Contentful Paint is very fast. Users will see content quickly, improving their initial perception of your site\'s speed.';
        } else if (value < 3.0) {
          return 'Good performance. Your First Contentful Paint is within acceptable limits. Consider optimizing critical rendering path to improve further.';
        } else {
          return 'Needs improvement. Your First Contentful Paint is slow, which can negatively impact user experience. Optimize server response time, eliminate render-blocking resources, and minimize critical CSS.';
        }

      case 'LCP':
        if (value < 2.5) {
          return 'Excellent! Your Largest Contentful Paint is optimal. The main content loads quickly, providing a great user experience.';
        } else if (value < 4.0) {
          return 'Good performance, but there\'s room for improvement. Optimize images, use a CDN, and improve server response time to get your LCP under 2.5 seconds.';
        } else {
          return 'Poor performance. Your Largest Contentful Paint is too slow, which significantly impacts user experience. Prioritize optimizing the largest element, improve server response time, and eliminate render-blocking resources.';
        }

      case 'FID':
      case 'INP':
        const threshold = metricName === 'INP' ? 200 : 100;
        const needsImprovement = metricName === 'INP' ? 500 : 300;
        if (value < threshold) {
          return `Excellent! Your ${metricName} is very fast. Users experience minimal delay when interacting with your site, creating a responsive feel.`;
        } else if (value < needsImprovement) {
          return `Good performance, but could be better. Your ${metricName} is acceptable but not optimal. Reduce JavaScript execution time, break up long tasks, and optimize event handlers.`;
        } else {
          return `Poor performance. Your ${metricName} is too high, causing noticeable delays when users interact with your site. Optimize JavaScript, reduce main thread work, and minimize event handler complexity.`;
        }

      case 'CLS':
        if (value < 0.1) {
          return 'Excellent! Your Cumulative Layout Shift is minimal. Your page has stable layout, preventing unexpected content shifts that frustrate users.';
        } else if (value < 0.25) {
          return 'Good performance, but there\'s room for improvement. Some layout shifts are occurring. Set size attributes on images and videos, avoid inserting content above existing content, and use transform animations.';
        } else {
          return 'Poor performance. Your Cumulative Layout Shift is high, causing visible content shifts that disrupt user experience. Set explicit dimensions on media, reserve space for ads and embeds, and avoid inserting content above existing content.';
        }

      case 'TBT':
        if (value < 200) {
          return 'Excellent! Your Total Blocking Time is very low. The main thread is not blocked for long periods, ensuring smooth interactivity.';
        } else if (value < 600) {
          return 'Good performance. Your Total Blocking Time is acceptable but could be improved. Break up long JavaScript tasks, optimize third-party scripts, and reduce JavaScript execution time.';
        } else {
          return 'Poor performance. Your Total Blocking Time is high, blocking the main thread and delaying interactivity. Optimize JavaScript, defer non-critical scripts, and reduce main thread work.';
        }

      case 'Speed Index':
        if (value < 3.4) {
          return 'Excellent! Your Speed Index is very fast. Content is visually displayed quickly, providing a great first impression.';
        } else if (value < 5.8) {
          return 'Good performance. Your Speed Index is acceptable but could be improved. Optimize images, reduce render-blocking resources, and improve server response time.';
        } else {
          return 'Poor performance. Your Speed Index is slow, meaning content takes too long to become visually complete. Optimize critical rendering path, reduce server response time, and eliminate render-blocking resources.';
        }

      default:
        if (score >= 90) {
          return 'Excellent performance! This metric is performing well.';
        } else if (score >= 50) {
          return 'Good performance, but there\'s room for improvement.';
        } else {
          return 'This metric needs improvement to provide a better user experience.';
        }
    }
  };

  const header = (
    <header className="header">
      <div className="header-container">
        <div className="header-actions">
          <a href="/pricing" className="header-menu-item">
            Plans & Pricing
          </a>
          {user && (
            <div className="user-info">
              <button
                className="user-icon-button"
                onClick={() => {
                  window.location.href = '/dashboard/account-settings';
                }}
                aria-label="Account settings"
                title="Account settings"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  return (
    <div className="core-web-vitals-page">
      {header}

      {/* Loading Screen Overlay */}
      {showLoadingScreen && (
        <div className="page-loader-overlay">
          <div className="page-loader-content">
            {funFact && (
              <div className="fun-fact-container">
                <div className="fun-fact-icon">💡</div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
            <div className="page-loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="page-loader-text">{loadingMessage || 'Loading...'}</p>
            <p className="page-loader-message">
              This will take a few seconds, depends on the website size. Kindly wait until our algorithms analyze your website.
            </p>
          </div>
        </div>
      )}

      <div className="container">
        <div className="core-web-vitals-header">
          <div>
            <h1 className="page-title">Core Web Vitals</h1>
            <p className="page-subtitle">Analyze your website's Core Web Vitals metrics</p>
          </div>
          <div className="header-actions">
            {/* Device View Toggle - Show when we have results for both or either device */}
            {(desktopResults || mobileResults) && (
              <div className="device-toggle-container">
                <div className="device-toggle">
                  <button
                    className={`device-toggle-btn ${viewDevice === 'desktop' ? 'active' : ''}`}
                    onClick={() => handleDeviceChange('desktop')}
                    disabled={loading || !desktopResults}
                    title="View Desktop Results"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                      <line x1="8" y1="21" x2="16" y2="21"></line>
                      <line x1="12" y1="17" x2="12" y2="21"></line>
                    </svg>
                    <span>Desktop</span>
                  </button>
                  <button
                    className={`device-toggle-btn ${viewDevice === 'mobile' ? 'active' : ''}`}
                    onClick={() => handleDeviceChange('mobile')}
                    disabled={loading || !mobileResults}
                    title="View Mobile Results"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                      <line x1="12" y1="18" x2="12.01" y2="18"></line>
                    </svg>
                    <span>Mobile</span>
                  </button>
                </div>
              </div>
            )}
            {results && (
              <>
                <button
                  className="re-analyze-btn"
                  onClick={handleReAnalyze}
                  disabled={loading || analyzeCountdown > 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Analyzing...
                    </>
                  ) : analyzeCountdown > 0 ? (
                    `Re-Analyze (${Math.floor(analyzeCountdown / 60)}:${String(analyzeCountdown % 60).padStart(2, '0')})`
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                      </svg>
                      Re-Analyze
                    </>
                  )}
                </button>
                <button 
                  className="download-report-btn" 
                  onClick={handleDownloadReport}
                  disabled={loading || downloadCountdown > 0}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  <span>
                    {downloadCountdown > 0 
                      ? `Download Report (${Math.floor(downloadCountdown / 60)}:${String(downloadCountdown % 60).padStart(2, '0')})`
                      : 'Download Report'}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {!results && (
          <div className="analysis-form">
            <form onSubmit={handleAnalyze}>
              <div className="form-group">
                <label htmlFor="url">Website URL</label>
                <input
                  type="text"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  disabled={loading}
                />
                <p className="form-hint">Analysis will be performed for both desktop and mobile devices</p>
              </div>
              <button type="submit" className="analyze-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Analyzing Desktop & Mobile...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    Analyze Core Web Vitals
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {error && (
          <div className="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {results && (
          <div ref={reportRef}>
            {/* Results Info */}
            <div className="results-info-header">
              <span><strong>URL:</strong> {results.url || url}</span>
              {results.strategy && (
                <span className="device-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {results.strategy === 'mobile' ? (
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                    ) : (
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    )}
                  </svg>
                  {results.strategy === 'mobile' ? 'Mobile' : 'Desktop'}
                </span>
              )}
              {results.timestamp && (
                <span className="timestamp">
                  Analyzed: {new Date(results.timestamp).toLocaleString()}
                </span>
              )}
            </div>

            {/* Category Scores */}
            {results.categoryScores && (
              <div className="category-scores-section">
                <div className="category-scores-grid">
                  {/* Performance */}
                  {(() => {
                    const score = results.categoryScores.performance;
                    const isValidScore = typeof score === 'number' && score >= 0 && score <= 100;
                    return isValidScore ? (
                      <div className="category-score-card">
                        <CircularProgress percentage={score} size={120} strokeWidth={8} />
                        <div className="category-score-label">Performance</div>
                        <div className="category-score-status" style={{ color: getScoreColor(score) }}>
                          {getScoreLabel(score)}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Accessibility */}
                  {(() => {
                    const score = results.categoryScores.accessibility;
                    const isValidScore = typeof score === 'number' && score >= 0 && score <= 100;
                    console.log('Accessibility render check:', { score, isValidScore, type: typeof score });
                    return isValidScore ? (
                      <div className="category-score-card">
                        <CircularProgress percentage={score} size={120} strokeWidth={8} />
                        <div className="category-score-label">Accessibility</div>
                        <div className="category-score-status" style={{ color: getScoreColor(score) }}>
                          {getScoreLabel(score)}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Best Practices */}
                  {(() => {
                    const score = results.categoryScores.bestPractices;
                    const isValidScore = typeof score === 'number' && score >= 0 && score <= 100;
                    console.log('Best Practices render check:', { score, isValidScore, type: typeof score });
                    return isValidScore ? (
                      <div className="category-score-card">
                        <CircularProgress percentage={score} size={120} strokeWidth={8} />
                        <div className="category-score-label">Best Practices</div>
                        <div className="category-score-status" style={{ color: getScoreColor(score) }}>
                          {getScoreLabel(score)}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* SEO */}
                  {(() => {
                    const score = results.categoryScores.seo;
                    const isValidScore = typeof score === 'number' && score >= 0 && score <= 100;
                    console.log('SEO render check:', { score, isValidScore, type: typeof score });
                    return isValidScore ? (
                      <div className="category-score-card">
                        <CircularProgress percentage={score} size={120} strokeWidth={8} />
                        <div className="category-score-label">SEO</div>
                        <div className="category-score-status" style={{ color: getScoreColor(score) }}>
                          {getScoreLabel(score)}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            )}

            {/* Performance Metrics */}
            <div className="vitals-section">
              <h3 className="section-title">Performance Metrics</h3>
              <div className="vitals-rows">
                {/* First Contentful Paint */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>FCP</h4>
                      <span className="vital-label">First Contentful Paint</span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor('FCP', results.firstContentfulPaint, 's') }}>
                      {results.firstContentfulPaint?.displayValue || formatMetric(results.firstContentfulPaint, 's')}
                    </div>
                    <div className="vital-description">
                      Time until the first content is rendered
                    </div>
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark('FCP', results.firstContentfulPaint, 's')}</p>
                    {getMetricThresholds('FCP').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds('FCP').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Largest Contentful Paint */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>LCP</h4>
                      <span className="vital-label">Largest Contentful Paint</span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor('LCP', results.lcp, 's') }}>
                      {results.lcp?.displayValue || formatMetric(results.lcp, 's')}
                    </div>
                    <div className="vital-status" style={{ color: getScoreColor(results.lcp?.score || 0) }}>
                      {getScoreLabel(results.lcp?.score || 0)}
                    </div>
                    {results.lcp?.source && (
                      <div className="vital-source" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        Data source: {results.lcp.source === 'field' ? 'Real user data' : 'Lab data'}
                      </div>
                    )}
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark('LCP', results.lcp, 's')}</p>
                    {getMetricThresholds('LCP').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds('LCP').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* FID/INP - First Input Delay or Interaction to Next Paint */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>{results.fid?.label || 'FID/INP'}</h4>
                      <span className="vital-label">
                        {results.fid?.label || 'First Input Delay / Interaction to Next Paint'}
                      </span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor(results.fid?.label?.includes('INP') ? 'INP' : 'FID', results.fid, 'ms') }}>
                      {formatMetric(results.fid, 'ms')}
                    </div>
                    <div className="vital-status" style={{ color: getScoreColor(results.fid?.score || 0) }}>
                      {getScoreLabel(results.fid?.score || 0)}
                    </div>
                    {results.fid?.source && (
                      <div className="vital-source" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        Data source: {results.fid.source === 'field' ? 'Real user data' : 'Lab data'}
                      </div>
                    )}
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark(results.fid?.label?.includes('INP') ? 'INP' : 'FID', results.fid, 'ms')}</p>
                    {getMetricThresholds(results.fid?.label?.includes('INP') ? 'INP' : 'FID').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds(results.fid?.label?.includes('INP') ? 'INP' : 'FID').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* CLS - Cumulative Layout Shift */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>CLS</h4>
                      <span className="vital-label">Cumulative Layout Shift</span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor('CLS', results.cls) }}>
                      {results.cls?.displayValue || formatMetric(results.cls)}
                    </div>
                    <div className="vital-status" style={{ color: getScoreColor(results.cls?.score || 0) }}>
                      {getScoreLabel(results.cls?.score || 0)}
                    </div>
                    {results.cls?.source && (
                      <div className="vital-source" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                        Data source: {results.cls.source === 'field' ? 'Real user data' : 'Lab data'}
                      </div>
                    )}
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark('CLS', results.cls)}</p>
                    {getMetricThresholds('CLS').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds('CLS').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Total Blocking Time */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>TBT</h4>
                      <span className="vital-label">Total Blocking Time</span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor('TBT', results.totalBlockingTime, 'ms') }}>
                      {results.totalBlockingTime?.displayValue || formatMetric(results.totalBlockingTime, 'ms')}
                    </div>
                    <div className="vital-description">
                      Sum of all blocking time between FCP and TTI
                    </div>
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark('TBT', results.totalBlockingTime, 'ms')}</p>
                    {getMetricThresholds('TBT').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds('TBT').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Speed Index */}
                <div className="vital-row">
                  <div className="vital-card">
                    <div className="vital-header">
                      <h4>Speed Index</h4>
                      <span className="vital-label">Speed Index</span>
                    </div>
                    <div className="vital-value" style={{ color: getMetricValueColor('Speed Index', results.speedIndex, 's') }}>
                      {results.speedIndex?.displayValue || formatMetric(results.speedIndex, 's')}
                    </div>
                    <div className="vital-description">
                      Speed at which content is visually displayed
                    </div>
                  </div>
                  <div className="vital-remark">
                    <p>{getMetricRemark('Speed Index', results.speedIndex, 's')}</p>
                    {getMetricThresholds('Speed Index').length > 0 && (
                      <table className="thresholds-table">
                        <thead>
                          <tr>
                            <th>Rating</th>
                            <th>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMetricThresholds('Speed Index').map((threshold, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="threshold-label" style={{ color: threshold.color }}>
                                  {threshold.label}
                                </span>
                              </td>
                              <td>{threshold.range}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Insights - Hidden for now, will be developed later */}
            {false && results.insights && (
              <div className="insights-section">
                <h3 className="section-title">Opportunities & Diagnostics</h3>
                
                {/* Performance Insights */}
                {results.insights.performance && results.insights.performance.length > 0 && (
                  <div className="insight-category">
                    <div 
                      className="insight-category-header"
                      onClick={() => toggleCategory('performance')}
                    >
                      <h4 className="insight-category-title">Performance</h4>
                      <svg 
                        className={`insight-category-chevron ${expandedCategories.has('performance') ? 'expanded' : ''}`}
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    {expandedCategories.has('performance') && (
                      <div className="insights-list">
                        {results.insights.performance.map((insight, index) => {
                          const insightId = insight.id || `performance-${index}`;
                          const isExpanded = expandedInsights.has(insightId);
                          const savingsText = getInsightSavingsText(insight);
                          
                          return (
                            <div key={insightId} className={`insight-item insight-${insight.type}`}>
                              <div 
                                className="insight-header-row"
                                onClick={() => toggleInsight(insightId)}
                              >
                                <div className="insight-header-content">
                                  <div className="insight-icon">
                                    {insight.type === 'opportunity' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                      </svg>
                                    ) : insight.type === 'diagnostic' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4M12 8h.01"></path>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="insight-title-content">
                                    <span className="insight-title-text">{insight.title}</span>
                                    {savingsText && (
                                      <span className="insight-savings-text">— {savingsText}</span>
                                    )}
                                  </div>
                                </div>
                                <svg 
                                  className={`insight-chevron ${isExpanded ? 'expanded' : ''}`}
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2"
                                >
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                              {isExpanded && (
                                <div className="insight-expanded-content">
                                  {insight.description && (
                                    <p className="insight-description">{insight.description}</p>
                                  )}
                                  {renderInsightDetails(insight)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Accessibility Insights */}
                {results.insights.accessibility && results.insights.accessibility.length > 0 && (
                  <div className="insight-category">
                    <div 
                      className="insight-category-header"
                      onClick={() => toggleCategory('accessibility')}
                    >
                      <h4 className="insight-category-title">Accessibility</h4>
                      <svg 
                        className={`insight-category-chevron ${expandedCategories.has('accessibility') ? 'expanded' : ''}`}
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    {expandedCategories.has('accessibility') && (
                      <div className="insights-list">
                        {results.insights.accessibility.map((insight, index) => {
                          const insightId = insight.id || `accessibility-${index}`;
                          const isExpanded = expandedInsights.has(insightId);
                          const savingsText = getInsightSavingsText(insight);
                          
                          return (
                            <div key={insightId} className={`insight-item insight-${insight.type}`}>
                              <div 
                                className="insight-header-row"
                                onClick={() => toggleInsight(insightId)}
                              >
                                <div className="insight-header-content">
                                  <div className="insight-icon">
                                    {insight.type === 'opportunity' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                      </svg>
                                    ) : insight.type === 'diagnostic' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4M12 8h.01"></path>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="insight-title-content">
                                    <span className="insight-title-text">{insight.title}</span>
                                    {savingsText && (
                                      <span className="insight-savings-text">— {savingsText}</span>
                                    )}
                                  </div>
                                </div>
                                <svg 
                                  className={`insight-chevron ${isExpanded ? 'expanded' : ''}`}
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2"
                                >
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                              {isExpanded && (
                                <div className="insight-expanded-content">
                                  {insight.description && (
                                    <p className="insight-description">{insight.description}</p>
                                  )}
                                  {renderInsightDetails(insight)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Best Practices Insights */}
                {results.insights.bestPractices && results.insights.bestPractices.length > 0 && (
                  <div className="insight-category">
                    <div 
                      className="insight-category-header"
                      onClick={() => toggleCategory('bestPractices')}
                    >
                      <h4 className="insight-category-title">Best Practices</h4>
                      <svg 
                        className={`insight-category-chevron ${expandedCategories.has('bestPractices') ? 'expanded' : ''}`}
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    {expandedCategories.has('bestPractices') && (
                      <div className="insights-list">
                        {results.insights.bestPractices.map((insight, index) => {
                          const insightId = insight.id || `bestPractices-${index}`;
                          const isExpanded = expandedInsights.has(insightId);
                          const savingsText = getInsightSavingsText(insight);
                          
                          return (
                            <div key={insightId} className={`insight-item insight-${insight.type}`}>
                              <div 
                                className="insight-header-row"
                                onClick={() => toggleInsight(insightId)}
                              >
                                <div className="insight-header-content">
                                  <div className="insight-icon">
                                    {insight.type === 'opportunity' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                      </svg>
                                    ) : insight.type === 'diagnostic' ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M12 16v-4M12 8h.01"></path>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="insight-title-content">
                                    <span className="insight-title-text">{insight.title}</span>
                                    {savingsText && (
                                      <span className="insight-savings-text">— {savingsText}</span>
                                    )}
                                  </div>
                                </div>
                                <svg 
                                  className={`insight-chevron ${isExpanded ? 'expanded' : ''}`}
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2"
                                >
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                              {isExpanded && (
                                <div className="insight-expanded-content">
                                  {insight.description && (
                                    <p className="insight-description">{insight.description}</p>
                                  )}
                                  {renderInsightDetails(insight)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* SEO Insights */}
                {results.insights.seo && results.insights.seo.length > 0 && (
                  <div className="insight-category">
                    <div 
                      className="insight-category-header"
                      onClick={() => toggleCategory('seo')}
                    >
                      <h4 className="insight-category-title">SEO</h4>
                      <svg 
                        className={`insight-category-chevron ${expandedCategories.has('seo') ? 'expanded' : ''}`}
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    {expandedCategories.has('seo') && (
                      <div className="insights-list">
                        {results.insights.seo.map((insight, index) => {
                        const insightId = insight.id || `seo-${index}`;
                        const isExpanded = expandedInsights.has(insightId);
                        const savingsText = getInsightSavingsText(insight);
                        
                        return (
                          <div key={insightId} className={`insight-item insight-${insight.type}`}>
                            <div 
                              className="insight-header-row"
                              onClick={() => toggleInsight(insightId)}
                            >
                              <div className="insight-header-content">
                                <div className="insight-icon">
                                  {insight.type === 'opportunity' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                      <line x1="12" y1="9" x2="12" y2="13"></line>
                                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                  ) : insight.type === 'diagnostic' ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <line x1="12" y1="16" x2="12" y2="12"></line>
                                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <path d="M12 16v-4M12 8h.01"></path>
                                    </svg>
                                  )}
                                </div>
                                <div className="insight-title-content">
                                  <span className="insight-title-text">{insight.title}</span>
                                  {savingsText && (
                                    <span className="insight-savings-text">— {savingsText}</span>
                                  )}
                                </div>
                              </div>
                              <svg 
                                className={`insight-chevron ${isExpanded ? 'expanded' : ''}`}
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </div>
                            {isExpanded && (
                              <div className="insight-expanded-content">
                                {insight.description && (
                                  <p className="insight-description">{insight.description}</p>
                                )}
                                {renderInsightDetails(insight)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoreWebVitals;

